import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErroreApi, chiama } from './cliente.js';

export interface Azienda {
  id: number;
  code: string;
  name: string;
}

export interface Utente {
  id: number;
  name: string;
  email: string;
  accessLevel: string;
  language: 'it' | 'en';
  theme: 'system' | 'light' | 'dark';
  /** La pagina da cui comincia, quando ne ha scelta una diversa da «Il mio lavoro». */
  homeScreen: string | null;
  companies: Azienda[];
  /** Le voci del menu che apre: vedi `permessi.ts`. */
  sections: string[];
}

export interface Sessione {
  user: Utente;
  companyId: number | null;
}

const CHIAVE = ['sessione'] as const;

/**
 * Chi è connesso. Se non c'è nessuno la risposta è `null`, non un errore:
 * "non sei entrato" è uno stato normale dell'applicazione, non un guasto.
 *
 * Ma lo dice il servizio, con un 401. Un servizio che non risponde è un
 * guasto: trattarlo come «non sei entrato» mostrava la pagina di accesso a
 * chi era dentro, che credeva di essere stato buttato fuori.
 */
export function useSessione() {
  return useQuery({
    queryKey: CHIAVE,
    queryFn: async (): Promise<Sessione | null> => {
      try {
        return await chiama<Sessione>('/api/me');
      } catch (errore) {
        if (errore instanceof ErroreApi && (errore.stato === 401 || errore.stato === 403)) return null;
        throw errore;
      }
    },
    staleTime: 30_000,
    // un servizio che si sta riavviando risponde dopo pochi secondi
    retry: 2,
  });
}

export function useAccesso() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: { email: string; password: string; companyId?: number; remember?: boolean }) =>
      chiama<{ user: Utente; companyId: number | null }>('/api/auth/login', {
        metodo: 'POST',
        corpo: dati,
      }),
    onSuccess: (esito) => {
      client.setQueryData(CHIAVE, { user: esito.user, companyId: esito.companyId });
    },
  });
}

/**
 * Cambiare azienda cambia **tutti** i dati: nessuna chiave delle letture
 * porta l'azienda, quindi quello che c'è in memoria è dell'azienda di prima e
 * va buttato. Senza, una pagina aperta mostra i dati vecchi mentre le
 * scritture vanno sulla nuova (Parametri salvati sull'azienda sbagliata,
 * «Chiudi tutti» degli avvisi col numero dell'altra). La pagina stessa la
 * rimonta `Applicazione`, così riparte anche quello che tiene per sé.
 */
export function useScegliAzienda() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (companyId: number) =>
      chiama<{ companyId: number }>('/api/auth/company', { metodo: 'POST', corpo: { companyId } }),
    onSuccess: async () => {
      await client.cancelQueries();
      await client.resetQueries({ predicate: (q) => q.queryKey[0] !== CHIAVE[0] });
      await client.invalidateQueries({ queryKey: CHIAVE });
    },
  });
}

export function useUscita() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => chiama<{ loggedOut: boolean }>('/api/auth/logout', { metodo: 'POST' }),
    onSuccess: () => {
      client.setQueryData(CHIAVE, null);
      client.clear();
    },
  });
}

export interface Preferenze {
  theme: Utente['theme'];
  language: Utente['language'];
  homeScreen: Utente['homeScreen'];
}

export function usePreferenze() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Partial<Preferenze>) =>
      chiama<Preferenze>('/api/me/preferences', {
        metodo: 'PATCH',
        corpo: dati,
      }),
    onSuccess: (esito) => {
      // aggiorna la sessione in memoria senza rileggere tutto
      client.setQueryData<Sessione | null>(CHIAVE, (vecchia) =>
        vecchia ? { ...vecchia, user: { ...vecchia.user, ...esito } } : vecchia,
      );
    },
  });
}
